import { z } from "zod/v4"
import safeJsonParse from "@/utils/safeParse"

// Each service declares, next to its fetchAPI call, the form parameters its endpoint reads in
// citixchange origin/develop (06aa41ba9d): a z.strictObject named `<method>RequestSchema`, cited as
// Controller.java:line. fetchAPI checks the data against it before sending and only reports a
// mismatch (see docs/api-contract-audit.md).
//
// The building blocks below are what the backend's RequestParser accepts for each kind of form
// parameter. fetchAPI sends the data object through qs.stringify, so the backend only ever sees
// strings: a number may be passed as a number or as the string it would stringify to.

const INTEGER_PATTERN = /^-?\d+$/
const DECIMAL_PATTERN = /^-?\d+(\.\d+)?$/

// getStringParameter cuts anything past maxLength and treats "" as missing.
export const stringParam = (maxLength: number) => z.string().max(maxLength)

// getIntParameter / getLongParameter: a value that does not parse falls back to the default.
export const intParam = z.union([z.number().int(), z.string().regex(INTEGER_PATTERN)])

// getDoubleParameter
export const numberParam = z.union([z.number(), z.string().regex(DECIMAL_PATTERN)])

// getBooleanParameter accepts true/false, 1/0, on/off and yes/no.
export const booleanParam = z.union([
  z.boolean(),
  z.literal(0),
  z.literal(1),
  z.enum(["true", "false", "1", "0", "on", "off", "yes", "no"])
])

// A parameter the backend parses as JSON: the client sends JSON.stringify of a value matching
// `schema`. The check parses the string back first. This transform is safe here, unlike in a
// response schema, because fetchAPI only looks at whether the request passes, never at the output.
export const jsonParam = <T extends z.ZodType>(schema: T) =>
  z
    .string()
    .transform(text => safeJsonParse<unknown>(text, undefined))
    .pipe(schema)

// Report parameters. ReportController is com/nv/controller/member/ReportController.java.

// Page size of the paged reports (FrontendUtils.getValidatedReportPageCount, SACRIC-1321): 1..20,
// default 20; anything else answers "page count exceeds limit". queryVendorReportDetail reads its
// own pageCount without this limit.
export const reportPageCountParam = z.number().int().min(1).max(20).optional()

// "yyyy-MM-dd HH:mm" in the account's time zone; a range is at most 14 days
export const dateTimeParam = z.string().regex(/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}$/)

// "yyyy-MM-dd"
export const dateParam = z.string().regex(/^\d{4}-\d{2}-\d{2}$/)

// userID is checked against ^[0-9a-zA-Z-]+$; most reports then query the session account
export const userIdParam = stringParam(20).regex(/^[0-9a-zA-Z-]+$/)

// ELK paging: the previous response's pageHits, a search-after key per page, read when
// pageNumber != 1 (ElkUtils.java:219-259)
export const totalHitsParam = jsonParam(z.record(z.string(), z.unknown())).optional()
